import React, { useState, useEffect } from 'react';
import esgPredictiveAnalyticsService from '../../../services/esg/esgPredictiveAnalyticsService';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * ESG Predictive Analytics Component
 * 
 * Modern predictive analytics platform for ESG performance optimization with AI/ML capabilities.
 * Features real-time forecasting, scenario modeling, risk prediction, and actionable insights.
 */
const ESGPredictiveAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTimeHorizon, setSelectedTimeHorizon] = useState('30d');

  useEffect(() => {
    const loadData = () => {
      setData({
        modelPortfolio: esgPredictiveAnalyticsService.getModelPortfolio(),
        mlModels: esgPredictiveAnalyticsService.getMLModels(),
        forecasting: esgPredictiveAnalyticsService.getForecasting(),
        scenarioAnalysis: esgPredictiveAnalyticsService.getScenarioAnalysis(),
        riskPredictions: esgPredictiveAnalyticsService.getRiskPredictions(),
        optimizationRecommendations: esgPredictiveAnalyticsService.getOptimizationRecommendations(),
        modelPerformance: esgPredictiveAnalyticsService.getModelPerformance(),
        advancedAnalytics: esgPredictiveAnalyticsService.getAdvancedAnalytics()
      });
      setLoading(false);
    };

    loadData();
    const unsubscribe = esgPredictiveAnalyticsService.subscribe(loadData);
    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mb-4"></div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading Predictive Analytics</h3>
          <p className="text-gray-600">Initializing AI/ML models and forecasting engines...</p>
        </div>
      </div>
    );
  }

  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316'];
  
  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'forecasting', name: 'Forecasting', icon: '🔮' },
    { id: 'models', name: 'ML Models', icon: '🤖' },
    { id: 'scenarios', name: 'Scenarios', icon: '🎯' },
    { id: 'risks', name: 'Risk Predictions', icon: '⚠️' },
    { id: 'insights', name: 'Insights', icon: '💡' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-2xl">
        <div className="container-fluid py-8 px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                  <span className="text-3xl">🔮</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold mb-1">Predictive Analytics</h1>
                  <p className="text-indigo-100 text-sm">AI-Powered ESG Performance Forecasting & Optimization</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <select 
                className="bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                value={selectedTimeHorizon}
                onChange={(e) => setSelectedTimeHorizon(e.target.value)}
              >
                <option value="7d">7 Days</option>
                <option value="30d">30 Days</option>
                <option value="90d">90 Days</option>
                <option value="1y">1 Year</option>
              </select>
              <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                🔄 Refresh Models
              </button>
              <button className="bg-white text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-lg">
                📥 Export Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container-fluid px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-fluid px-6 py-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'forecasting' && renderForecasting()}
        {activeTab === 'models' && renderModels()}
        {activeTab === 'scenarios' && renderScenarios()}
        {activeTab === 'risks' && renderRisks()}
        {activeTab === 'insights' && renderInsights()}
      </div>
    </div>
  );

  function renderOverview() {
    const mockData = data.modelPortfolio || { summary: { totalModels: 12, activeModels: 10, averageAccuracy: 94.5, totalPredictions: 125000 }};
    
    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                <span className="text-2xl">🤖</span>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Active</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">{mockData.summary.activeModels}</h3>
            <p className="text-gray-600 text-sm mb-2">Active ML Models</p>
            <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
              <span>↗</span>
              <span>+3 this month</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl">
                <span className="text-2xl">🎯</span>
              </div>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Excellent</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">{mockData.summary.averageAccuracy}%</h3>
            <p className="text-gray-600 text-sm mb-2">Average Accuracy</p>
            <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
              <span>↗</span>
              <span>+2.3% improved</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-gradient-to-br from-pink-500 to-pink-600 p-3 rounded-xl">
                <span className="text-2xl">📊</span>
              </div>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Daily</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">{(mockData.summary.totalPredictions / 1000).toFixed(1)}K</h3>
            <p className="text-gray-600 text-sm mb-2">Predictions Today</p>
            <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
              <span>↗</span>
              <span>15.7K daily avg</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl">
                <span className="text-2xl">⚡</span>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Real-time</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">98.7%</h3>
            <p className="text-gray-600 text-sm mb-2">System Uptime</p>
            <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
              <span>✓</span>
              <span>99.9% SLA target</span>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Model Performance Trend */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Model Accuracy Trends</h3>
                <p className="text-sm text-gray-600">Last 30 days performance metrics</p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">↗ +2.3%</span>
            </div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateTrendData()}>
                  <defs>
                    <linearGradient id="colorAccuracy" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" domain={[80, 100]} />
                  <Tooltip />
                  <Area type="monotone" dataKey="accuracy" stroke="#6366f1" fillOpacity={1} fill="url(#colorAccuracy)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Prediction Volume */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Prediction Volume</h3>
                <p className="text-sm text-gray-600">Daily prediction requests</p>
              </div>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">↗ +18%</span>
            </div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={generateVolumeData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Bar dataKey="predictions" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Model Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Active Model Categories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'Environmental Impact', models: 4, accuracy: 96.2, color: 'from-green-500 to-emerald-600', icon: '🌱' },
                { name: 'Social Responsibility', models: 3, accuracy: 93.8, color: 'from-blue-500 to-indigo-600', icon: '👥' },
                { name: 'Governance & Ethics', models: 3, accuracy: 95.1, color: 'from-purple-500 to-pink-600', icon: '⚖️' },
                { name: 'Risk Assessment', models: 2, accuracy: 94.5, color: 'from-orange-500 to-red-600', icon: '⚠️' }
              ].map((category, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`bg-gradient-to-r ${category.color} p-2 rounded-lg`}>
                      <span className="text-xl">{category.icon}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 text-sm">{category.name}</h4>
                      <p className="text-xs text-gray-600">{category.models} models active</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Accuracy:</span>
                    <span className="text-sm font-bold text-green-600">{category.accuracy}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className={`bg-gradient-to-r ${category.color} h-2 rounded-full`} style={{ width: `${category.accuracy}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-3 rounded-xl font-medium text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2">
                <span>🚀</span>
                <span>Run New Prediction</span>
              </button>
              <button className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2">
                <span>🔧</span>
                <span>Configure Models</span>
              </button>
              <button className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2">
                <span>📊</span>
                <span>View Analytics</span>
              </button>
              <button className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2">
                <span>📥</span>
                <span>Export Data</span>
              </button>
            </div>
            <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-2">
                <span className="text-blue-600 text-lg">💡</span>
                <div>
                  <h4 className="font-semibold text-blue-900 text-sm mb-1">Optimization Tip</h4>
                  <p className="text-xs text-blue-700">Consider retraining Environmental Impact models with latest data for improved accuracy.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderForecasting() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ESG Performance Forecasting</h2>
          <p className="text-gray-600 mb-6">AI-powered multi-horizon forecasting for Environmental, Social, and Governance metrics</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {[
              { metric: 'Carbon Emissions', current: 4250, forecast: 3825, change: -10, period: '6 months', color: 'green', icon: '🌍' },
              { metric: 'Employee Diversity', current: 68, forecast: 75, change: +10, period: '1 year', color: 'blue', icon: '👥' },
              { metric: 'Board Independence', current: 72, forecast: 80, change: +11, period: '1 year', color: 'purple', icon: '⚖️' }
            ].map((item, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl">{item.icon}</span>
                  <span className={`px-3 py-1 bg-${item.color}-100 text-${item.color}-700 text-xs font-semibold rounded-full`}>
                    {item.change > 0 ? '↗' : '↘'} {Math.abs(item.change)}%
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.metric}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current:</span>
                    <span className="font-semibold">{item.current}{item.metric.includes('Emissions') ? ' tCO2e' : '%'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Forecast ({item.period}):</span>
                    <span className={`font-semibold text-${item.color}-600`}>{item.forecast}{item.metric.includes('Emissions') ? ' tCO2e' : '%'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={generateForecastData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="environmental" stroke="#10b981" strokeWidth={3} dot={{ r: 5 }} name="Environmental Score" />
                <Line type="monotone" dataKey="social" stroke="#3b82f6" strokeWidth={3} dot={{ r: 5 }} name="Social Score" />
                <Line type="monotone" dataKey="governance" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 5 }} name="Governance Score" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  function renderModels() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Machine Learning Models</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Carbon Footprint Predictor', type: 'Regression', accuracy: 96.2, status: 'active', lastTrained: '2 days ago' },
              { name: 'Diversity Classifier', type: 'Classification', accuracy: 93.8, status: 'active', lastTrained: '5 days ago' },
              { name: 'Governance Risk Scorer', type: 'Ensemble', accuracy: 95.1, status: 'active', lastTrained: '1 day ago' },
              { name: 'ESG Compliance Detector', type: 'Deep Learning', accuracy: 94.5, status: 'training', lastTrained: '3 hours ago' },
              { name: 'Supply Chain Impact', type: 'Random Forest', accuracy: 92.7, status: 'active', lastTrained: '1 week ago' },
              { name: 'Stakeholder Sentiment', type: 'NLP', accuracy: 91.3, status: 'active', lastTrained: '4 days ago' }
            ].map((model, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-gray-900 text-sm">{model.name}</h3>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    model.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {model.status}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{model.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Accuracy:</span>
                    <span className="font-bold text-green-600">{model.accuracy}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Trained:</span>
                    <span className="text-gray-500">{model.lastTrained}</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all">
                    Deploy
                  </button>
                  <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-xs font-medium transition-all">
                    Retrain
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderScenarios() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Scenario Analysis</h2>
          <p className="text-gray-600 mb-6">Explore what-if scenarios and their potential impact on ESG performance</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              { 
                name: 'Aggressive Carbon Reduction', 
                description: 'Reduce emissions by 30% in 18 months', 
                impact: { environmental: +25, social: +5, governance: +10 },
                feasibility: 72,
                cost: '$2.5M'
              },
              { 
                name: 'Diversity Initiative Expansion', 
                description: 'Increase diverse hires by 40%', 
                impact: { environmental: +2, social: +30, governance: +8 },
                feasibility: 85,
                cost: '$1.2M'
              },
              { 
                name: 'Board Restructuring', 
                description: 'Add 3 independent board members', 
                impact: { environmental: +3, social: +5, governance: +28 },
                feasibility: 65,
                cost: '$800K'
              },
              { 
                name: 'Supply Chain Transformation', 
                description: 'Switch to 80% sustainable suppliers', 
                impact: { environmental: +18, social: +12, governance: +15 },
                feasibility: 58,
                cost: '$3.8M'
              }
            ].map((scenario, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{scenario.name}</h3>
                    <p className="text-sm text-gray-600">{scenario.description}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    scenario.feasibility >= 70 ? 'bg-green-100 text-green-700' : 
                    scenario.feasibility >= 60 ? 'bg-yellow-100 text-yellow-700' : 
                    'bg-red-100 text-red-700'
                  }`}>
                    {scenario.feasibility}% feasible
                  </span>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Environmental Impact</span>
                      <span className="font-semibold text-green-600">+{scenario.impact.environmental}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${scenario.impact.environmental * 2}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Social Impact</span>
                      <span className="font-semibold text-blue-600">+{scenario.impact.social}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${scenario.impact.social * 2}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Governance Impact</span>
                      <span className="font-semibold text-purple-600">+{scenario.impact.governance}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${scenario.impact.governance * 2}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Estimated Cost: <span className="font-bold text-gray-900">{scenario.cost}</span></span>
                  <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-xs font-medium transition-all">
                    Run Simulation
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderRisks() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Risk Predictions</h2>
          <p className="text-gray-600 mb-6">AI-powered early warning system for ESG-related risks</p>
          
          <div className="grid grid-cols-1 gap-4">
            {[
              { risk: 'Regulatory Compliance Gap', probability: 78, severity: 'High', category: 'Governance', eta: '3 months', mitigation: 'Policy update required' },
              { risk: 'Supply Chain Disruption', probability: 65, severity: 'Medium', category: 'Environmental', eta: '6 months', mitigation: 'Diversify suppliers' },
              { risk: 'Employee Turnover Spike', probability: 45, severity: 'Medium', category: 'Social', eta: '4 months', mitigation: 'Retention program' },
              { risk: 'Carbon Target Miss', probability: 38, severity: 'High', category: 'Environmental', eta: '1 year', mitigation: 'Accelerate initiatives' },
              { risk: 'Board Diversity Gap', probability: 52, severity: 'Low', category: 'Governance', eta: '8 months', mitigation: 'Recruitment campaign' }
            ].map((risk, idx) => (
              <div key={idx} className={`border-l-4 rounded-xl p-5 ${
                risk.severity === 'High' ? 'border-red-500 bg-red-50' :
                risk.severity === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                'border-blue-500 bg-blue-50'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-gray-900">{risk.risk}</h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        risk.severity === 'High' ? 'bg-red-200 text-red-800' :
                        risk.severity === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
                        'bg-blue-200 text-blue-800'
                      }`}>
                        {risk.severity}
                      </span>
                      <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                        {risk.category}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Probability:</span>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div className={`h-2 rounded-full ${
                              risk.probability >= 70 ? 'bg-red-500' :
                              risk.probability >= 50 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`} style={{ width: `${risk.probability}%` }}></div>
                          </div>
                          <span className="font-bold">{risk.probability}%</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">ETA:</span>
                        <p className="font-semibold mt-1">{risk.eta}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Mitigation:</span>
                        <p className="font-semibold mt-1">{risk.mitigation}</p>
                      </div>
                    </div>
                  </div>
                  <button className="ml-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderInsights() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">AI-Powered Insights</h2>
          <p className="text-gray-600 mb-6">Actionable recommendations based on predictive analytics</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'Optimize Carbon Initiatives',
                priority: 'High',
                confidence: 92,
                description: 'Models predict 23% better ROI if carbon reduction efforts focus on supply chain optimization rather than operational changes.',
                actions: ['Review supplier contracts', 'Implement green logistics', 'Set new targets'],
                impact: '+$1.2M savings'
              },
              {
                title: 'Accelerate Diversity Programs',
                priority: 'Medium',
                confidence: 87,
                description: 'Current trajectory will miss 2025 diversity targets by 12%. Early intervention can close this gap.',
                actions: ['Expand recruitment channels', 'Enhance retention programs', 'Update policies'],
                impact: '+15% improvement'
              },
              {
                title: 'Strengthen Board Governance',
                priority: 'Medium',
                confidence: 85,
                description: 'Adding 2 independent directors with ESG expertise could improve governance score by 18 points.',
                actions: ['Identify candidates', 'Update board structure', 'Revise policies'],
                impact: '+18 points'
              },
              {
                title: 'Risk Mitigation Strategy',
                priority: 'High',
                confidence: 89,
                description: 'Proactive compliance measures now can reduce regulatory risk exposure by 34% over next 12 months.',
                actions: ['Audit current practices', 'Implement controls', 'Train staff'],
                impact: '-34% risk'
              }
            ].map((insight, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-gray-900 flex-1">{insight.title}</h3>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ml-2 ${
                    insight.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {insight.priority} Priority
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{insight.description}</p>
                
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-gray-600">AI Confidence</span>
                    <span className="font-bold text-green-600">{insight.confidence}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full" style={{ width: `${insight.confidence}%` }}></div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <p className="text-xs font-semibold text-gray-700">Recommended Actions:</p>
                  {insight.actions.map((action, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="text-indigo-600">✓</span>
                      <span className="text-gray-700">{action}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Expected Impact:</span>
                    <span className="font-bold text-green-600">{insight.impact}</span>
                  </div>
                  <button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all">
                    Implement
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for chart data
  function generateTrendData() {
    const data = [];
    for (let i = 0; i < 30; i++) {
      data.push({
        date: `Day ${i + 1}`,
        accuracy: 90 + Math.random() * 8 + (i * 0.1)
      });
    }
    return data;
  }

  function generateVolumeData() {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => ({
      day,
      predictions: Math.floor(10000 + Math.random() * 8000)
    }));
  }

  function generateForecastData() {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, idx) => ({
      month,
      environmental: 70 + Math.random() * 20 + idx,
      social: 65 + Math.random() * 25 + idx * 0.8,
      governance: 75 + Math.random() * 15 + idx * 0.6
    }));
  }
};

export default ESGPredictiveAnalytics;
